import React, { useState, useCallback, useEffect } from 'react';
import { FormModal } from './FormModal';
import { RichTextEditor } from './RichTextEditor';
import { InfoTooltip } from './InfoTooltip';
import * as api from '../services/apiClient';
import { logger } from '../utils/logger';
import { cleanEmptyHtml } from '../shared/htmlUtils';

export interface ClauseVersionFormData {
  name: string;
  content: string;
  selection_criteria: string;
}

interface ClauseVersionModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'add' | 'edit';
  initialData?: ClauseVersionFormData;
  versionId?: number;  // Required in edit mode (non-embedded)
  clauseId?: number;   // Required in add mode (non-embedded)
  onSubmitSuccess?: () => void;
  // Embedded mode props - for use within ClauseTypeModal
  embeddedMode?: boolean;
  defaultName?: string;
  onSaveLocal?: (data: ClauseVersionFormData) => void;
}

const defaultFormData: ClauseVersionFormData = {
  name: '',
  content: '',
  selection_criteria: '',
};

export const ClauseVersionModal: React.FC<ClauseVersionModalProps> = ({
  isOpen,
  onClose,
  mode,
  initialData,
  versionId,
  clauseId,
  onSubmitSuccess,
  embeddedMode,
  defaultName,
  onSaveLocal,
}) => {
  const [formData, setFormData] = useState<ClauseVersionFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && initialData) {
        setFormData(initialData);
      } else if (mode === 'add' && defaultName) {
        setFormData({ ...defaultFormData, name: defaultName });
      } else {
        setFormData(defaultFormData);
      }
      setError(null);
    }
  }, [isOpen, mode, initialData, defaultName]);

  const updateField = useCallback(<K extends keyof ClauseVersionFormData>(
    field: K,
    value: ClauseVersionFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.name.trim()) {
      setError('Le nom de la version est requis');
      return;
    }

    if (!cleanEmptyHtml(formData.content)) {
      setError('Le contenu de la version est requis');
      return;
    }

    // Embedded mode: just return the data locally, no API call
    if (embeddedMode) {
      if (typeof onSaveLocal !== 'function') {
        setError('Erreur de configuration: onSaveLocal non fourni en mode embedded');
        logger.error('embeddedMode is true but onSaveLocal is not a function', { embeddedMode, onSaveLocal, formData });
        return;
      }
      onSaveLocal(formData);
      onClose();
      return;
    }

    // Non-embedded mode: validate IDs and call API
    if (mode === 'add' && !clauseId) {
      setError('Erreur: clause ID manquant');
      return;
    }

    if (mode === 'edit' && !versionId) {
      setError('Erreur: version ID manquant');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      let response;

      if (mode === 'edit' && versionId) {
        response = await api.updateClauseVersion(versionId, {
          name: formData.name,
          content: cleanEmptyHtml(formData.content),
          selection_criteria: cleanEmptyHtml(formData.selection_criteria),
        });
      } else if (mode === 'add' && clauseId) {
        response = await api.createClauseVersion({
          clause_type_id: clauseId,
          name: formData.name,
          content: cleanEmptyHtml(formData.content) || undefined,
          selection_criteria: cleanEmptyHtml(formData.selection_criteria) || undefined,
        });
      }

      if (response?.success) {
        logger.info(`Version ${mode === 'edit' ? 'modifiée' : 'créée'}: ${mode === 'edit' ? versionId : 'nouvelle'}`);
        onSubmitSuccess?.();
        onClose();
      } else {
        setError(response?.error || 'Erreur lors de la sauvegarde');
        logger.error('Erreur sauvegarde version:', response?.error);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erreur inconnue';
      setError(errorMessage);
      logger.error('Erreur sauvegarde version:', err);
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, versionId, clauseId, formData, onClose, onSubmitSuccess, embeddedMode, onSaveLocal]);

  const title = mode === 'add' ? 'Ajouter une version' : 'Modifier la version';

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      submitDisabled={!formData.name.trim() || !formData.content.trim()}
    >
      {error && (
        <div className="form-error-message">
          {error}
        </div>
      )}

      <div className="form-field">
        <label htmlFor="version-name" className="form-field-label">
          Nom de la version *
        </label>
        <input
          id="version-name"
          type="text"
          className="form-field-input"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          placeholder="Ex: Version standard"
          required
          disabled={isSubmitting}
        />
      </div>

      <div className="form-field">
        <div className="form-field-label-row">
          <label className="form-field-label">
            Contenu *
          </label>
          <InfoTooltip ariaLabel="Informations sur les variables">
            <p><strong>Variables</strong></p>
            <p>
              Vous pouvez insérer des variables dans le contenu de la clause
              en utilisant le bouton "Variable" dans la barre d'outils.
            </p>
            <p>
              Si vous choisissez une variable déjà utilisée ailleurs dans le contrat,
              l'IA ne posera la question qu'une seule fois et réutilisera
              automatiquement la même réponse partout où cette variable apparaît.
            </p>
          </InfoTooltip>
        </div>
        <RichTextEditor
          value={formData.content}
          onChange={(html) => updateField('content', html)}
          placeholder="Contenu de la version..."
          clauseId={clauseId}
          enableVariables={true}
        />
      </div>

      <div className="form-field">
        <label className="form-field-label">
          Critères de sélection
        </label>
        <RichTextEditor
          value={formData.selection_criteria}
          onChange={(html) => updateField('selection_criteria', html)}
          placeholder="Critères pour sélectionner cette version..."
        />
      </div>
    </FormModal>
  );
};
